import React from 'react';
import {saveAs} from 'file-saver';
import XLSX from 'xlsx';
import moment from 'moment';
import {concat} from 'lodash';
import {formatSecondsSmall, toHumanDateTime} from '../utils/date';
import {numberFormat} from '../utils/number';

const getParamsData = (params, data, hasIContracts = false) => {
    const array = [];

    array.push([window.__js_general.search_parameters]);

    array.push([
        window.__js_dashboard.date_from,
        toHumanDateTime(params.dateFrom)
    ]);
    array.push([
        window.__js_dashboard.date_to,
        toHumanDateTime(params.dateTo)
    ]);
    array.push([
        window.__js_dashboard.department,
        params.department
            ? params.department.label
            : window.__js_general.all_departments
    ]);

    if (hasIContracts) {
        array.push([
            window.__js_dashboard.rfq_process,
            params.rfqProcess
                ? params.rfqProcess.label
                : window.__js_general.all_rfq_processes
        ]);
    }

    return concat(array, ['']);
}

const getHeadingCountsData = (data) => {
    const totalCount = data.competitions_total_count || 0;
    const totalBudget = data.competitions_total_budget || 0.0;
    const totalAwarded = data.competitions_total_awarded || 0.0;
    const avgCompletionTime = data.competitions_avg_completion_time
        ? formatSecondsSmall(data.competitions_avg_completion_time)
        : '-';

    const array = [];

    array.push([
        window.__js_dashboard.competitions_total_count,
        numberFormat(totalCount, 0, '', '.'),
    ]);
    array.push([
        window.__js_dashboard.total_budget_amount,
        numberFormat(totalBudget, 2, ',', '.') + '€',
    ]);
    array.push([
        window.__js_dashboard.total_awarded_amount,
        numberFormat(totalAwarded, 2, ',', '.') + '€',
    ]);
    array.push([
        window.__js_dashboard.avg_completion_time,
        avgCompletionTime,
    ]);

    return concat(array, ['']);
}

const getTop10SuppliersData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.top_10_suppliers]);

    array.push([
        'Α/Α',
        window.__js_dashboard.supplier_company_name,
        window.__js_dashboard.supplier_vat_number,
        window.__js_dashboard.awarded_amount,
    ]);

    data.forEach((item, index) => {
        array.push([
            index + 1,
            item.company_name,
            item.vat_number,
            numberFormat(item.total, 2, ',', '.') + '€',
        ]);
    });

    return concat(array, ['']);
}

const getCompetitionsByDepartmentData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.competitions_by_department]);

    array.push([
        'Α/Α',
        window.__js_dashboard.department,
        window.__js_dashboard.competitions_count,
        window.__js_dashboard.total_budget,
    ]);

    data.forEach((item, index) => {
        array.push([
            index + 1,
            item.name,
            numberFormat(item.competitions_count, 0, '', '.'),
            numberFormat(item.budget_sum, 2, ',', '.') + '€',
        ]);
    });

    return concat(array, ['']);
}

const getCompetitionsByTypeData = (data, accountEnabledCompetitionTypes) => {
    if (accountEnabledCompetitionTypes.length <= 1) {
        return [];
    }

    const array = [];

    array.push([window.__js_dashboard.competitions_by_type]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getCompetitionsWithNegotiationData = (data, accountHasNegotiation) => {
    if (!accountHasNegotiation) {
        return [];
    }

    const array = [];

    array.push([window.__js_dashboard.competitions_with_negotiation]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getCompetitionRoundsImprovementData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.competition_rounds_improvement]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getNumberOfContractsData = (total, data) => {
    let newTotal = total ? total.num_contracts : 0;
    newTotal = numberFormat(newTotal, 0, '', '.');
    const heading = `${window.__js_dashboard.number_of_contracts} (${window.__js_dashboard.total}: ${newTotal})`;

    const array = [];

    array.push([heading]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getContractAmountsData = (total, data) => {
    let newTotal = total ? total.total_amount : 0.0;
    newTotal = numberFormat(newTotal, 2, ',', '.');
    const heading = `${window.__js_dashboard.total_contract_amount} (${window.__js_dashboard.total}: ${newTotal} €)`;

    const array = [];

    array.push([heading]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getTendersVsDaNumberData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.tenders_vs_da_number]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getTendersVsDaValueData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.tenders_vs_da_value]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getTopSuppliersContractValueData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.top_suppliers_contract_value]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getSavingsAmountByYearData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.savings_amount + ' ' + window.__js_dashboard.by_year]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getSavingsAmountByDepartmentData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.savings_amount + ' ' + window.__js_dashboard.by_department]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getSavingsAmountByRfqProcessData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.savings_amount + ' ' + window.__js_dashboard.by_rfq_process]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getCompetitionCompletionTimeByRfqProcessData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.competition_completion_time_by_rfq_process]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getContractCreationTimeByYearData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.contract_creation_time + ' ' + window.__js_dashboard.by_year]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getContractCreationTimeByDepartmentData = (data) => {
    const array = [];

    array.push([window.__js_dashboard.contract_creation_time + ' ' + window.__js_dashboard.by_department]);

    data.labels.forEach((item, index) => {
        array.push([
            index + 1,
            data.labels[index],
            data.datasets[0].data[index],
        ]);
    });

    return concat(array, ['']);
}

const getContractsData = (data, hasIContracts = false) => {
    if (!hasIContracts) {
        return [];
    }

    return concat(
        getNumberOfContractsData(data.totalNumberOfContracts, data.numberOfContracts),
        getContractAmountsData(data.totalContractAmount, data.contractAmounts),
        getTendersVsDaNumberData(data.tendersVsDaNumber),
        getTendersVsDaValueData(data.tendersVsDaValue),
        getTopSuppliersContractValueData(data.topSuppliersContractValue),
        getSavingsAmountByYearData(data.savingsAmountByYear),
        getSavingsAmountByDepartmentData(data.savingsAmountByDepartment),
        getSavingsAmountByRfqProcessData(data.savingsAmountByRfqProcess),
        getCompetitionCompletionTimeByRfqProcessData(data.competitionCompletionTimeByRfqProcess),
        getContractCreationTimeByYearData(data.contractCreationTimeByYear),
        getContractCreationTimeByDepartmentData(data.contractCreationTimeByDepartment),
    );
}

const getExcelSheetData = (
    params,
    data,
    accountEnabledCompetitionTypes,
    accountHasNegotiation,
    hasIContracts = false,
) => {
    return concat(
        getParamsData(params, data, hasIContracts),
        getHeadingCountsData(data.counts),
        getTop10SuppliersData(data.top10Suppliers),
        getCompetitionsByDepartmentData(data.competitionsByDepartment),
        getCompetitionsByTypeData(data.competitionsByType, accountEnabledCompetitionTypes),
        getCompetitionsWithNegotiationData(data.competitionsByNegotiation, accountHasNegotiation),
        getContractsData(data, hasIContracts),
        getCompetitionRoundsImprovementData(data.competitionRoundsImprovement),
    );
}

const ExcelExport = ({
                         params,
                         data,
                         style = {},
                         hasIContracts = false,
                         accountEnabledCompetitionTypes,
                         accountHasNegotiation,
                     }) => {
    const exportToExcel = (e) => {
        e.preventDefault();
        const sheetData = getExcelSheetData(
            params,
            data,
            accountEnabledCompetitionTypes,
            accountHasNegotiation,
            hasIContracts,
        );
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Στατιστικά Στοιχεία');
        const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
        const blob = new Blob([excelBuffer], {type: 'application/octet-stream'});
        const fileName = `iSupplies - Στατιστικά Στοιχεία - ${toHumanDateTime(moment())}.xlsx`;
        saveAs(blob, fileName);
    };

    return (
        <button className="btn btn-success" onClick={exportToExcel} style={style}>
            <i className="fa fa-fw fa-file-excel-o"></i> {window.__js_general.excel_export}
        </button>
    );
}

export default ExcelExport;